import React, { useState, useEffect } from 'react'
import { FormControl, MenuItem, Select, TableCell, TableRow, Typography, Box, IconButton } from '@mui/material'
import { CloseTwoTone } from '@mui/icons-material'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const FileUploadRow = ({
  importType,
  availableColumns,
  samples,
  onChange,
  isUploading,
  selectedColumns,
  rowIndex,
  isOr,
  isRowDisabled
}) => {
  const [selectedColumn, setSelectedColumn] = useState('')
  const [open, setOpen] = useState(false)

  // Reset selected column when availableColumns change (new file upload)
  useEffect(() => {
    setSelectedColumn('')
  }, [availableColumns])

  const handleChange = (event) => {
    const columnName = event.target.value
    setSelectedColumn(columnName)
    onChange(columnName, importType.value, rowIndex)
  }

  const handleClearSelection = (event) => {
    // Stop propagation to prevent the dropdown from opening
    if (event) {
      event.stopPropagation()
    }
    setSelectedColumn('')
    onChange('', importType.value, rowIndex)
  }

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 240
      }
    }
  }

  // Get sample data for selected column
  const getSampleForColumn = (columnName) => {
    const columnIndex = availableColumns.findIndex(col => col === columnName)
    return columnIndex >= 0 ? samples[columnIndex] : ''
  }

  // If this is an "or" separator, render it instead of a normal row
  if (isOr) {
    return (
      <TableRow>
        <TableCell colSpan={3} align='center' sx={{ padding: '10px 0' }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          >
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Typography sx={{
              mx: 2,
              color: '#666',
              fontWeight: 600,
              fontSize: '14px'
            }}
            >
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  // Using a custom component for the selection field to include the clear button
  const CustomSelect = () => (
    <Box sx={{ position: 'relative' }}>
      <FormControl fullWidth size='small' sx={{ maxHeight: '300px' }}>
        <Select
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedColumn}
          onChange={handleChange}
          variant='outlined'
          MenuProps={MenuProps}
          disabled={isUploading || isRowDisabled}
          displayEmpty
        >
          <MenuItem value='' disabled>Select a column</MenuItem>
          {availableColumns.map((column, index) => (
            <MenuItem
              value={column}
              key={index}
              disabled={selectedColumns.includes(column) && selectedColumn !== column}
            >
              {column}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Clear button absolute positioned over the select */}
      {selectedColumn && (
        <IconButton
          size='small'
          onClick={handleClearSelection}
          disabled={isUploading || isRowDisabled}
          sx={{
            position: 'absolute',
            right: 28,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '2px',
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          <CloseTwoTone fontSize='small' />
        </IconButton>
      )}
    </Box>
  )

  return (
    <TableRow sx={{ opacity: isRowDisabled ? 0.5 : 1 }}>
      {/* First column: Fixed import type (Email, LinkedIn, etc.) */}
      <TableCell>
        <Typography sx={{ fontWeight: 500 }}>
          {importType.label}
        </Typography>
      </TableCell>

      {/* Second column: CSV column selection dropdown with clear button */}
      <TableCell width={240}>
        <CustomSelect />
      </TableCell>

      {/* Third column: Sample data from selected column */}
      <TableCell>
        {selectedColumn
          ? (
            <Typography>
              {getSampleForColumn(selectedColumn)}
            </Typography>
            )
          : null}
      </TableCell>
    </TableRow>
  )
}

export default FileUploadRow
